import { useEffect, useRef, useState } from "react";
import { api, errText, media, mfaTrust } from "../../lib/api";
import { Phone, MessageCircle, Camera, AlertTriangle, LogOut, Bell, BellRing, ChevronDown, Menu } from "lucide-react";
import { initPwa, enablePush, currentPushState } from "../../lib/push";
import { ApplicationStatus } from "./FloristApply";
import ReadOnlyCatalogue from "./ReadOnlyCatalogue";
import Payouts from "./Payouts";
import CourierRun from "./CourierRun";
import FloristDrivers, { DeliveryModeCard } from "./FloristDrivers";
import AccountProfileCard from "./AccountProfileCard";
import { ForgotPasswordLink } from "../../components/ForgotPasswordLink";
import { fmtDate } from "../../components/DateField";
import { MfaChallenge, SecurityPanel } from "../../components/Mfa";
import LegalGate from "./LegalGate";
import { ZoomImage } from "../../components/ZoomImage";

const L = {
  fr: { login: "Espace fleuriste", email: "E-mail", password: "Mot de passe", signIn: "Se connecter", orders: "Commandes", catalog: "Catalogue", availability: "Disponibilité", zones: "Zones", earnings: "Revenus", performance: "Performance", tabs: { new: "Nouvelles", in_progress: "En cours", out_for_delivery: "En livraison", delivered: "Livrées", issues: "Problèmes" }, accept: "Accepter", decline: "Refuser", payout: "Votre part", expires: "Expire dans", preparing: "En préparation", ready: "Prête", out: "En livraison", delivered: "Livrée avec photo", uploadPhoto: "Photo de livraison obligatoire", takePhoto: "Prendre la photo et livrer", problem: "Signaler un problème", capacity: "Capacité par jour", cutoff: "Heure limite", pause: "Suspendre les nouvelles commandes", reason: "Motif", save: "Enregistrer", served: "Desservie", fee: "Frais (MAD)", pending: "En attente de paiement", nextPayout: "Prochain versement", commission: "Commission", net: "Net", logout: "Déconnexion", noOrders: "Aucune commande ici pour le moment.", recipient: "Destinataire", landmark: "Repère", message: "Message de la carte", writeIn: "À écrire en", call: "Appeler", whatsapp: "WhatsApp", newProduct: "Nouveau produit", title: "Titre", price: "Prix MAD", description: "Description", submit: "Soumettre à validation", substitution: "J'accepte la politique de substitution", photos: "Photos (2 minimum)", status: "Statut" },
};

const IssueTypes = [
  ["recipient_not_answering", "Destinataire injoignable"],
  ["address_wrong", "Adresse erronée"],
  ["recipient_not_home", "Destinataire absent"],
  ["refused_delivery", "Livraison refusée"],
  ["flower_unavailable", "Fleur indisponible"],
];

// Grouped florist navigation — parents shown bold, children collapse until the parent is tapped.
const FLORIST_GROUPS = [
  ["Commandes", [["orders", "Commandes"], ["courier", "Livraisons"], ["drivers", "Livreurs"]]],
  ["Catalogue", [["catalog", "Mes bouquets"], ["availability", "Disponibilité"], ["zones", "Zones"]]],
  ["Finances", [["earnings", "Revenus"], ["payouts", "Versements"], ["performance", "Performance"]]],
  ["Boutique", [["compte", "Mon compte"]]],
];
const FLORIST_GROUP_OF = (view) => (FLORIST_GROUPS.find(([, items]) => items.some(([k]) => k === view)) || [])[0];

// Shop-front photo card — lets a shop replace the storefront photo drivers see at pickup.
const ShopfrontCard = ({ url, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const upload = async (file) => {
    if (!file) return;
    setErr(""); setBusy(true);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      await api.post("/florist/pickup-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChanged && onChanged();
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card p-4 space-y-3" data-testid="florist-shopfront-card">
      <div>
        <h3 className="font-display text-lg text-ink">Photo de la devanture</h3>
        <p className="text-xs text-muted">Les livreurs partenaires la voient au retrait pour reconnaître la bonne porte. Gardez-la à jour si votre façade change.</p>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 items-start">
        {url ? (
          <img src={media(url)} alt="Devanture de la boutique" data-testid="florist-shopfront-preview"
               className="w-44 h-32 object-cover border border-brass-light rounded-sm" />
        ) : (
          <div className="w-44 h-32 border border-dashed border-brass-light rounded-sm flex items-center justify-center text-muted" data-testid="florist-shopfront-empty">
            <Camera size={22} />
          </div>
        )}
        <label className="btn-ghost text-xs py-2 px-3 cursor-pointer inline-flex items-center gap-2">
          <Camera size={13} />
          {busy ? "Chargement…" : url ? "Changer la photo" : "Charger une photo"}
          <input type="file" accept="image/*" className="hidden" data-testid="florist-shopfront-upload"
                 onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
        </label>
      </div>
      {err ? <p className="text-danger text-xs" data-testid="florist-shopfront-error">{err}</p> : null}
    </div>
  );
};

const Countdown = ({ iso }) => {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const tick = () => setLeft(Math.max(0, Math.floor((new Date(iso) - new Date()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [iso]);
  return <span className="tabular">{Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")}</span>;
};

// Persistent yellow strip visible only when an admin has signed in as this shop.
// A single click restores the admin token and bounces back to /admin.
const ImpersonationBanner = () => {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem("fl_impersonating");
      return raw && localStorage.getItem("fl_admin_token") ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  if (!state) return null;
  const back = () => {
    const admin = localStorage.getItem("fl_admin_token");
    // Restore the admin token to BOTH slots so AdminApp auto-logs in on landing
    // (it reads `fl_admin_token` to bootstrap the session and mirrors it into
    // `fl_token` for the shared API client).
    if (admin) localStorage.setItem("fl_token", admin);
    localStorage.removeItem("fl_impersonating");
    window.location.href = `/admin/florists/${state.florist_id}`;
  };
  return (
    <div className="bg-brass text-ink text-xs px-4 py-2 flex items-center justify-between gap-3 sticky top-0 z-30"
         data-testid="impersonation-banner">
      <span>Admin session — connecté en tant que <b>{state.shop_name}</b>. Toutes vos actions sont enregistrées.</span>
      <button className="underline underline-offset-2 font-medium" data-testid="impersonation-back"
              onClick={back}>← Retour à l'admin</button>
    </div>
  );
};


export default function FloristApp() {
  const t = L.fr;
  const [user, setUser] = useState(null);
  // While the very first /auth/me is in flight we render a neutral splash instead
  // of the login form — otherwise the login page briefly flashes on every refresh.
  const [booted, setBooted] = useState(() => !localStorage.getItem("fl_token"));
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [view, setView] = useState("orders");
  const [floristMenuOpen, setFloristMenuOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => new Set(["Commandes"]));
  useEffect(() => { const g = FLORIST_GROUP_OF(view); if (g) setOpenGroups((s) => new Set(s).add(g)); }, [view]);
  const [tab, setTab] = useState("new");
  const [orders, setOrders] = useState([]);
  const [detail, setDetail] = useState(null);
  const [dash, setDash] = useState(null);
  const [pushState, setPushState] = useState("off");
  useEffect(() => { if (user) { initPwa("florist"); currentPushState().then(setPushState); } }, [user]);
  const [meta, setMeta] = useState({ occasions: [], productTypes: [] });
  const [avail, setAvail] = useState(null);
  const [bwDate, setBwDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [zones, setZones] = useState([]);
  const [earn, setEarn] = useState(null);
  const [newProduct, setNewProduct] = useState({ title: "", base_price_mad: "", description: "", product_type: "hand-tied", images: [], substitution_policy_accepted: false });
  const [screen, setScreen] = useState("login");
  const [mfa, setMfa] = useState(null);
  const [sec, setSec] = useState(null);
  const fileRef = useRef();

  const reloadMe = () => api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});

  useEffect(() => {
    if (!localStorage.getItem("fl_token")) return;
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem("fl_token"))
      .finally(() => setBooted(true));
  }, []);

  const loadOrders = () => api.get("/florist/orders", { params: { tab } }).then((r) => setOrders(r.data)).catch(() => {});

  const isShopStaff = user && ["florist", "florist_staff"].includes(user.user?.role);

  useEffect(() => {
    if (!isShopStaff) return undefined;
    api.get("/florist/dashboard").then((r) => setDash(r.data)).catch(() => {});
    loadOrders();
    const id = setInterval(loadOrders, 20000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShopStaff, tab]);

  useEffect(() => {
    if (!isShopStaff) return;
    if (view === "catalog") {
      api.get("/bootstrap", { params: { locale: "fr" } }).then((r) => setMeta({
        occasions: (r.data.occasions || []).map((o) => ({ id: o.id, name: o.name })),
        productTypes: r.data.product_types || [],
      })).catch(() => {});
    }
    if (view === "availability") api.get("/florist/availability").then((r) => setAvail(r.data));
    if (view === "zones") api.get("/florist/zones").then((r) => setZones(r.data));
    if (view === "earnings") api.get("/florist/earnings").then((r) => setEarn(r.data));
    if (view === "compte") api.get("/auth/mfa/status").then((r) => setSec(r.data)).catch(() => {});
  }, [view, isShopStaff]);

  const finishLogin = (data) => {
    if (!["florist", "florist_staff", "florist_applicant"].includes(data.user.role)) {
      setError("Compte non fleuriste"); return;
    }
    mfaTrust.save(data);
    localStorage.setItem("fl_token", data.token);
    setMfa(null);
    setUser(data);
  };

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.post("/auth/login", { ...creds, mfa_trust: mfaTrust.get() });
      if (data.mfa_required) {
        setMfa({ challenge: data.challenge_token, method: data.method, hint: data.destination_hint });
        return;
      }
      finishLogin(data);
    } catch (err) {
      setError(errText(err));
    }
  };

  const loadSecurity = () => api.get("/auth/mfa/status").then((r) => setSec(r.data)).catch(() => {});

  if (!user && !booted)
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center" data-testid="florist-boot">
        <div className="text-plaster/70 text-sm font-display animate-pulse">Fleurinelle</div>
      </div>
    );

  if (!user)
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center p-6" data-testid="florist-login">
        {mfa ? (
          <div className="bg-plaster p-8 w-full max-w-sm border border-brass">
            <MfaChallenge challenge={mfa.challenge} method={mfa.method} hint={mfa.hint}
                          onVerified={finishLogin} onCancel={() => { setMfa(null); setError(""); }} />
          </div>
        ) : (
          <form onSubmit={login} className="bg-plaster p-8 w-full max-w-sm space-y-4 border border-brass">
            <h1 className="font-display text-2xl text-ink">{t.login}</h1>
            <input data-testid="florist-email" className="field" placeholder={t.email} value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} />
            <input data-testid="florist-password" type="password" className="field" placeholder={t.password} value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} />
            {error ? <p className="text-danger text-sm" data-testid="florist-login-error">{error}</p> : null}
            <button data-testid="florist-login-button" className="btn-primary w-full">{t.signIn}</button>
            <ForgotPasswordLink locale="fr" email={creds.email} testid="florist-forgot" />
          </form>
        )}
      </div>
    );

  if (user.user?.role === "florist_applicant")
    return (
      <div className="min-h-screen bg-plaster" data-testid="applicant-screen">
        <header className="bg-ink text-plaster px-4 py-3 flex items-center gap-3">
          <img src="/brand/fleurinelle-header-dark.png" alt="Fleurinelle" width="1200" height="360" className="h-7 w-auto block select-none" draggable="false" />
          <span className="text-xs text-plaster/70">{user.user.email}</span>
        </header>
        <ApplicationStatus
          onSignOut={() => {
            localStorage.removeItem("fl_token");
            setUser(null);
            setScreen("login");
          }}
        />
      </div>
    );

  // Admins impersonating a shop must never be blocked by the shop's pending T&Cs
  // — only the shop's real owner can bind their business to new legal terms.
  if ((user.user?.pending_legal || []).length && !user.user?.impersonator_admin_id)
    return <LegalGate locale={user.user?.locale === "ar" ? "ar" : "fr"} onAccepted={reloadMe} />;

  // First-time plan chooser: shown right after admin approval, before the shop ever
  // sees the dashboard. Admins impersonating never see this — it's a shop-owner
  // decision that binds the business to a commission rate.
  // Curated network: plans no longer apply — the chooser is skipped.

  const act = async (fn) => {
    try {
      await fn();
      await loadOrders();
      setDetail(null);
      const d = await api.get("/florist/dashboard");
      setDash(d.data);
    } catch (e) {
      alert(errText(e));
    }
  };

  const openDetail = async (id) => {
    const { data } = await api.get(`/florist/orders/${id}`);
    setDetail(data);
  };

  const deliver = async (orderId, file) => {
    const fd = new FormData();
    fd.append("photo", file);
    await api.post(`/florist/orders/${orderId}/proof`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  };

  return (
    <div className="min-h-screen bg-plaster" data-testid="florist-dashboard">
      <ImpersonationBanner />
      <header className="bg-ink text-plaster px-4 py-3 flex items-center gap-3 sticky top-0 z-20">
        <img src="/brand/fleurinelle-header-dark.png" alt="Fleurinelle" width="1200" height="360" className="h-7 w-auto block select-none" draggable="false" />
        <span className="text-xs text-plaster/70">{user.florist?.shop_name}</span>
        <button className="md:hidden flex items-center gap-1.5 text-xs text-plaster border border-plaster/25 rounded px-2.5 py-1.5"
                data-testid="florist-menu-toggle" onClick={() => setFloristMenuOpen((o) => !o)}>
          <Menu size={14} /> Menu
        </button>
        <button className="ms-auto p-2" data-testid="florist-push-toggle" title="Notifications"
                onClick={async () => { const ok = await enablePush(); setPushState(ok ? "on" : "denied"); }}>
          {pushState === "on" ? <BellRing size={16} className="text-brass" /> : <Bell size={16} />}
        </button>
        <button
          className="p-2"
          data-testid="florist-logout"
          onClick={() => {
            localStorage.removeItem("fl_token");
            setUser(null);
          }}
        >
          <LogOut size={16} />
        </button>
      </header>

      <div className="md:flex">
        <nav className={`${floristMenuOpen ? "block" : "hidden"} md:block w-full md:w-60 shrink-0 border-b md:border-b-0 md:border-e border-brass-light bg-plaster-deep md:min-h-[calc(100vh-52px)]`}
             data-testid="florist-nav">
          {(() => {
            const role = user.user?.role;
            const staffRole = user.user?.staff_role;
            const isOwner = role === "florist";
            const isManager = staffRole === "manager";
            const allow = (k) => {
              if (["orders", "courier", "catalog", "compte"].includes(k)) return true;
              if (k === "drivers") return isOwner && dash?.delivery_mode === "self";
              if (k === "team") return isOwner;
              return isOwner || isManager;
            };
            const toggle = (name) => setOpenGroups((s) => { const n = new Set(s); n.has(name) ? n.delete(name) : n.add(name); return n; });
            return FLORIST_GROUPS.map(([name, items]) => {
              const vis = items.filter(([k]) => allow(k));
              if (!vis.length) return null;
              const open = openGroups.has(name);
              return (
                <div key={name} className="border-b border-brass-light/50">
                  <button data-testid={`florist-navgroup-${name}`} onClick={() => toggle(name)}
                          className="w-full flex items-center justify-between px-4 py-3 text-sm font-bold text-ink">
                    <span>{name}</span>
                    <ChevronDown size={16} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open ? (
                    <div className="pb-1">
                      {vis.map(([k, label]) => (
                        <button key={k} data-testid={`florist-nav-${k}`}
                                onClick={() => { setView(k); setFloristMenuOpen(false); }}
                                className={`w-full text-start ps-7 pe-4 py-2 text-sm transition-colors ${view === k ? "text-ink font-medium border-s-2 border-brass bg-plaster" : "text-muted hover:text-ink"}`}>
                          {label}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            });
          })()}
        </nav>

        <main className="flex-1 min-w-0 p-4">
          <div className="max-w-3xl mx-auto space-y-4">
        {dash?.onboarding ? (
          <div className="card p-4 border-warning" data-testid="onboarding-banner">
            <p className="text-sm text-ink">Commande test d'intégration</p>
            <p className="text-xs text-muted mt-1">
              Acceptez la commande test, composez-la et livrez-la avec une photo. Votre boutique passe en ligne dès que
              l'équipe Fleurinelle voit la photo de livraison.
            </p>
          </div>
        ) : null}
        {view === "courier" ? <CourierRun /> : null}
        {view === "drivers" ? <FloristDrivers /> : null}
        {view === "orders" ? (
          <>
            <div className="flex overflow-x-auto snap-x-strip gap-2">
              {Object.entries(t.tabs).map(([k, label]) => (
                <button key={k} data-testid={`florist-tab-${k}`} onClick={() => setTab(k)} className={`text-xs px-3 py-2 border ${tab === k ? "border-ink bg-plaster-deep text-ink" : "border-brass-light text-muted"}`}>
                  {label} {dash?.counts?.[k] ? `(${dash.counts[k]})` : ""}
                </button>
              ))}
            </div>
            {orders.length === 0 ? <p className="text-sm text-muted py-8">{t.noOrders}</p> : null}
            {orders.map((o) => (
              <article key={o.id} className="card p-4 space-y-3" data-testid={`florist-order-${o.order_number}`}>
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <p className="smallcaps">{o.order_number}</p>
                    <p className="text-sm text-ink">{fmtDate(o.delivery_date)} · {o.delivery_window_label}</p>
                  </div>
                  <p className="text-sm tabular text-ink">{o.payout_mad} MAD</p>
                </div>
                <div className="flex gap-3">
                  {o.items.map((i) => (
                    <ZoomImage key={i.image} src={i.image} alt={i.title} caption={`${i.title} · ${i.variant || ""}`.trim()}
                               className="w-16 aspect-[4/5] shrink-0" testid={`florist-order-photo-${o.id}`} />
                  ))}
                  <div className="text-xs text-muted">
                    {o.items.map((i) => (
                      <p key={i.title}>{i.title} · {i.variant} × {i.quantity}</p>
                    ))}
                    {o.addons.map((a) => (
                      <p key={a.name}>+ {a.name}</p>
                    ))}
                  </div>
                </div>
                {o.status === "awaiting_acceptance" ? (
                  <>
                    {o.offer_expires_at ? <p className="text-xs text-warning">{t.expires} <Countdown iso={o.offer_expires_at} /></p>
                                        : <p className="text-xs text-brass" data-testid={`awaiting-${o.order_number}`}>En attente de votre acceptation — merci de confirmer dès que possible.</p>}
                    <div className="grid grid-cols-2 gap-3">
                      <button data-testid={`accept-${o.order_number}`} className="btn-primary" onClick={() => act(() => api.post(`/florist/orders/${o.id}/accept`))}>{t.accept}</button>
                      <button data-testid={`decline-${o.order_number}`} className="btn-ghost" onClick={() => act(() => api.post(`/florist/orders/${o.id}/decline`, { reason: "indisponible" }))}>{t.decline}</button>
                    </div>
                  </>
                ) : (
                  <button data-testid={`open-${o.order_number}`} className="btn-ghost w-full" onClick={() => openDetail(o.id)}>{o.status_label}</button>
                )}
              </article>
            ))}
          </>
        ) : null}

        {view === "catalog" ? <ReadOnlyCatalogue /> : null}

        {view === "payouts" ? <Payouts /> : null}

        {view === "availability" && avail ? (
          <div className="card p-4 space-y-4" data-testid="florist-availability">
            <label className="block text-sm">
              {t.capacity}
              <input className="field mt-1" data-testid="capacity-input" type="number" value={avail.capacity_per_day} onChange={(e) => setAvail({ ...avail, capacity_per_day: Number(e.target.value) })} />
            </label>
            <label className="block text-sm">
              {t.cutoff}
              <input className="field mt-1" data-testid="cutoff-input" value={avail.cutoff} onChange={(e) => setAvail({ ...avail, cutoff: e.target.value })} />
            </label>
            <label className="flex gap-2 text-sm items-center">
              <input type="checkbox" data-testid="pause-toggle" checked={avail.paused} onChange={(e) => setAvail({ ...avail, paused: e.target.checked })} />
              {t.pause}
            </label>
            {avail.paused ? <input className="field" data-testid="pause-reason" placeholder={t.reason} value={avail.pause_reason} onChange={(e) => setAvail({ ...avail, pause_reason: e.target.value })} /> : null}

            <div className="border-t border-brass-light pt-4 space-y-2" data-testid="recurring-blackouts">
              <p className="smallcaps">Règle hebdomadaire</p>
              <p className="text-xs text-muted">Fermez le même créneau chaque semaine, sans avoir à choisir chaque date (ex. jamais de soir le dimanche).</p>
              <div className="grid grid-cols-1 sm:grid-cols-7 gap-2">
                {[["mon", "Lun"], ["tue", "Mar"], ["wed", "Mer"], ["thu", "Jeu"], ["fri", "Ven"], ["sat", "Sam"], ["sun", "Dim"]].map(([wd, lbl]) => {
                  const rb = avail.recurring_blackouts || {};
                  const wins = rb[wd] || [];
                  return (
                    <div key={wd} className="border border-brass-light p-2 space-y-1" data-testid={`recurring-day-${wd}`}>
                      <p className="smallcaps text-brass">{lbl}</p>
                      {[["morning", "Matin"], ["afternoon", "Après-midi"], ["evening", "Soir"]].map(([w, wLbl]) => {
                        const on = wins.includes(w);
                        return (
                          <button key={w} type="button" data-testid={`recurring-toggle-${wd}-${w}`}
                                  className={`w-full text-[11px] rounded-sm border px-2 py-1 transition-colors ${on ? "bg-danger/10 border-danger text-danger" : "border-brass-light text-muted hover:text-ink"}`}
                                  onClick={() => {
                                    const map = { ...(avail.recurring_blackouts || {}) };
                                    const list = new Set(map[wd] || []);
                                    list.has(w) ? list.delete(w) : list.add(w);
                                    if (list.size) map[wd] = [...list]; else delete map[wd];
                                    setAvail({ ...avail, recurring_blackouts: map });
                                  }}>
                            {on ? `${wLbl} · fermé` : wLbl}
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="border-t border-brass-light pt-4 space-y-2" data-testid="blackout-windows">
              <p className="smallcaps">Fermer un créneau précis</p>
              <p className="text-xs text-muted">Fermez un seul créneau pour une date sans fermer toute la journée.</p>
              <input className="field" type="date" data-testid="blackout-date" min={new Date().toISOString().slice(0, 10)}
                     value={bwDate} onChange={(e) => setBwDate(e.target.value)} />
              <div className="flex gap-2">
                {[["morning", "Matin"], ["afternoon", "Après-midi"], ["evening", "Soir"]].map(([w, lbl]) => {
                  const bw = avail.blackout_windows || {};
                  const closed = (bw[bwDate] || []).includes(w);
                  return (
                    <button key={w} type="button" data-testid={`blackout-toggle-${w}`}
                            className={`flex-1 rounded-full border px-3 py-2 text-xs transition-colors ${closed ? "bg-danger/10 border-danger text-danger" : "border-brass text-muted hover:text-ink"}`}
                            onClick={() => {
                              const map = { ...(avail.blackout_windows || {}) };
                              const list = new Set(map[bwDate] || []);
                              list.has(w) ? list.delete(w) : list.add(w);
                              if (list.size) map[bwDate] = [...list]; else delete map[bwDate];
                              setAvail({ ...avail, blackout_windows: map });
                            }}>
                      {closed ? `${lbl} · fermé` : lbl}
                    </button>
                  );
                })}
              </div>
              {Object.keys(avail.blackout_windows || {}).length ? (
                <div className="space-y-1 pt-1" data-testid="blackout-list">
                  {Object.entries(avail.blackout_windows).sort().map(([d, wins]) => (
                    <p key={d} className="text-xs text-charcoal flex items-center justify-between border-b border-brass-light py-1">
                      <span>{d} · {wins.map((w) => ({ morning: "Matin", afternoon: "Après-midi", evening: "Soir" }[w])).join(", ")}</span>
                      <button type="button" data-testid={`blackout-clear-${d}`} className="text-danger text-xs"
                              onClick={() => { const map = { ...avail.blackout_windows }; delete map[d]; setAvail({ ...avail, blackout_windows: map }); }}>✕</button>
                    </p>
                  ))}
                </div>
              ) : null}
            </div>

            <button className="btn-primary w-full" data-testid="availability-save" onClick={async () => {
              try {
                await api.patch("/florist/availability", { capacity_per_day: avail.capacity_per_day, cutoff: avail.cutoff, paused: avail.paused, pause_reason: avail.pause_reason, blackout_windows: avail.blackout_windows || {}, recurring_blackouts: avail.recurring_blackouts || {} });
                alert("OK");
              } catch (e) {
                alert(errText(e));
              }
            }}>{t.save}</button>
          </div>
        ) : null}

        {view === "zones" ? (
          <div className="space-y-2" data-testid="florist-zones">
            {zones.map((z) => (
              <div key={z.id} className="card p-3 flex items-center gap-3 text-sm">
                <input type="checkbox" data-testid={`zone-toggle-${z.id}`} checked={z.served} onChange={async (e) => {
                  await api.patch("/florist/zones", { zone_id: z.id, served: e.target.checked });
                  const r = await api.get("/florist/zones");
                  setZones(r.data);
                }} />
                <span className="flex-1">{z.name}</span>
                <span className="tabular text-muted" data-testid={`zone-estimate-${z.id}`}>{z.est_minutes}′</span>
              </div>
            ))}
          </div>
        ) : null}

        {view === "earnings" && earn ? (
          <div className="space-y-4" data-testid="florist-earnings">
            <div className="card p-4 text-sm space-y-1">
              <p className="smallcaps">{t.pending}</p>
              <p className="font-display text-3xl text-ink tabular">{earn.pending_mad} MAD</p>
              <p className="text-xs text-muted" data-testid="florist-next-payout">{t.nextPayout}: {earn.next_payout_date}</p>
              <p className="text-xs text-muted">{earn.bank?.bank_name} ····{earn.bank?.rib_last4}</p>
              <button className="btn-primary text-xs mt-2 w-full sm:w-fit" data-testid="florist-goto-payouts"
                      onClick={() => setView("payouts")}>
                Demander un versement →
              </button>
            </div>
            {earn.payouts?.length ? (
              <div className="space-y-2" data-testid="florist-payouts">
                <p className="smallcaps">Relevés hebdomadaires</p>
                {earn.payouts.map((p) => (
                  <div key={p.id} className="card p-3 text-xs flex items-center gap-3" data-testid={`payout-${p.id}`}>
                    <span className="flex-1">{p.period_start} → {p.period_end} · {p.order_numbers?.length || 0} commandes</span>
                    <span className="tabular">{p.net_mad} MAD</span>
                    <span className="text-muted">{p.status === "paid" ? "payé" : "en attente"}</span>
                    <button className="btn-ghost text-[11px] py-1 px-2" data-testid={`payout-statement-${p.id}`} onClick={async () => {
                      const r = await api.get(`/florist/payouts/${p.id}/statement`, { responseType: "blob" });
                      const url = URL.createObjectURL(new Blob([r.data], { type: "text/html" }));
                      window.open(url, "_blank");
                    }}>Relevé</button>
                  </div>
                ))}
              </div>
            ) : null}
            {earn.rows.map((r) => (
              <div key={r.order_number} className="card p-3 text-xs flex justify-between" data-testid={`earning-${r.order_number}`}>
                <span>{r.order_number} · {fmtDate(r.date)}</span>
                <span className="tabular">
                  <b data-testid={`earning-payout-${r.order_number}`}>{r.payout_mad}</b> MAD
                </span>
              </div>
            ))}
          </div>
        ) : null}


        {view === "compte" ? (
          <div data-testid="florist-security-view">
            <h2 className="font-display text-xl text-ink mb-1">Mon compte</h2>
            <p className="text-sm text-muted mb-4">Informations de la boutique, coordonnées et sécurité.</p>
            {user.user?.role === "florist" ? (
              <div className="mb-4">
                <AccountProfileCard />
              </div>
            ) : null}
            {user.user?.role === "florist" && dash ? (
              <div className="mb-4">
                <DeliveryModeCard mode={dash.delivery_mode} onChanged={() => api.get("/florist/dashboard").then((r) => setDash(r.data)).catch(() => {})} />
              </div>
            ) : null}
            {user.user?.role === "florist" && dash ? (
              <div className="mb-4">
                <ShopfrontCard url={dash.pickup_photo_url} onChanged={() => api.get("/florist/dashboard").then((r) => setDash(r.data)).catch(() => {})} />
              </div>
            ) : null}
            {sec ? <SecurityPanel status={sec} lang="fr" onStatusChange={() => api.get("/auth/mfa/status").then((r) => setSec(r.data)).catch(() => {})} /> : null}
          </div>
        ) : null}

        {view === "performance" && dash ? (
          <div className="card p-5 space-y-3 text-sm" data-testid="florist-performance">
            {Object.entries(dash.performance).map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-brass-light/60 pb-2">
                <span className="text-muted">{k}</span>
                <span className="tabular text-ink">{typeof v === "number" && v <= 1 && k.includes("rate") ? `${Math.round(v * 100)}%` : v}</span>
              </div>
            ))}
            <p className="text-xs text-success">
              {dash.performance.acceptance_rate > 0.85 && dash.performance.on_time_rate > 0.9 ? "Excellent — priorité de routage maximale" : "Correct — améliorez le taux d'acceptation pour recevoir plus de commandes"}
            </p>
          </div>
        ) : null}
          </div>
        </main>
      </div>

      {detail ? (
        <div className="fixed inset-0 scrim z-30 flex items-end sm:items-center justify-center p-0 sm:p-6" data-testid="florist-order-detail">
          <div className="bg-plaster w-full sm:max-w-md max-h-[92vh] overflow-y-auto p-5 space-y-4">
            <div className="flex justify-between">
              <p className="smallcaps">{detail.order_number}</p>
              <button onClick={() => setDetail(null)} data-testid="close-detail">✕</button>
            </div>
            <div className="card p-3 text-xs space-y-1" data-testid="florist-delivery-facts">
              <p className="text-ink tabular"><b>{fmtDate(detail.delivery_date)}</b> · {detail.delivery_window_label}</p>
              <p className="text-ink">Zone : <b>{detail.zone_name || "—"}</b></p>
              <p className="text-muted" data-testid="florist-detail-payout">Montant à recevoir : {detail.payout_mad} MAD</p>
            </div>
            <div className="text-sm space-y-1">
              <p className="text-ink font-medium">{detail.recipient_name}</p>
              <p className="text-muted">{detail.recipient_address}</p>
              <p className="text-muted">{t.landmark}: <b>{detail.landmark_note || "—"}</b></p>
              {detail.delivery_location ? (
                <a data-testid="florist-map-link" target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 text-xs text-ink underline underline-offset-4 decoration-brass"
                   href={`https://www.google.com/maps/search/?api=1&query=${detail.delivery_location.lat},${detail.delivery_location.lng}`}>
                  📍 Localisation exacte — ouvrir dans Maps
                </a>
              ) : null}
              <p className="text-xs text-warning">{detail.call_before_delivery ? "☎ Appeler avant la livraison" : "🤫 Ne pas appeler — surprise"}</p>
              {detail.special_instructions ? (
                <p className="text-xs text-danger" data-testid="florist-special-instructions">⚠ {detail.special_instructions}</p>
              ) : null}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <a href={`tel:${detail.recipient_phone}`} className="btn-ghost flex items-center justify-center gap-2" data-testid="call-recipient"><Phone size={14} />{t.call}</a>
              <a href={`https://wa.me/${(detail.recipient_whatsapp || detail.recipient_phone || "").replace(/\D/g, "")}`} className="btn-ghost flex items-center justify-center gap-2" data-testid="whatsapp-recipient"><MessageCircle size={14} />{t.whatsapp}</a>
              <button className="btn-ghost text-[11px]" data-testid="print-slip" onClick={() => window.print()}>Fiche livraison</button>
            </div>
            <div className="card p-3 text-sm">
              <p className="smallcaps">Composition du bouquet</p>
              <div className="mt-2 space-y-2">
                {detail.items.map((i) => (
                  <div key={i.title + i.variant} className="flex items-start gap-3">
                    {i.image ? <ZoomImage src={i.image} alt={i.title} caption={`${i.title} · ${i.variant || ""}`.trim()}
                                          className="w-14 aspect-[4/5] shrink-0" testid="florist-detail-photo" /> : null}
                    <div className="text-xs">
                      <p className="text-ink"><b>{i.title}</b> · {i.variant} × {i.quantity}</p>
                    </div>
                  </div>
                ))}
                {detail.addons.map((a) => (<p key={a.name} className="text-xs text-muted">+ {a.name} × {a.quantity}</p>))}
              </div>
            </div>
            <div className="card p-3 text-sm">
              <p className="smallcaps">{t.message} · {t.writeIn} {detail.card_language}</p>
              <p className="mt-2 whitespace-pre-line">{detail.card_message || "—"}</p>
              {detail.is_anonymous ? <p className="text-xs text-muted mt-1">Message anonyme — ne pas révéler l'expéditeur.</p> : null}
            </div>
            <div className="grid gap-2">
              {["preparing", "ready", "out_for_delivery"].map((s) => (
                <button key={s} data-testid={`status-${s}`} className="btn-ghost" onClick={() => act(() => api.patch(`/florist/orders/${detail.id}/status`, { status: s }))}>
                  {s === "preparing" ? t.preparing : s === "ready" ? t.ready : t.out}
                </button>
              ))}
              <p className="text-xs text-warning flex items-center gap-2"><Camera size={13} />{t.uploadPhoto}</p>
              <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="proof-file-input" onChange={(e) => e.target.files[0] && act(() => deliver(detail.id, e.target.files[0]))} />
              <button className="btn-primary" data-testid="deliver-button" onClick={() => fileRef.current?.click()}>{t.takePhoto}</button>
            </div>
            {detail.delivery_channel === "self" ? (
              <div className="card p-3 text-sm space-y-2" data-testid="delivery-ops">
                <p className="smallcaps">Livraison</p>
                {detail.driver_name ? (
                  <p className="text-xs text-ink">Livreur assigné : <b>{detail.driver_name}</b></p>
                ) : <p className="text-xs text-muted">Vos livreurs voient cette commande sur /driver dès qu'elle est « Prête ».</p>}
                <button data-testid="push-to-network" className="btn-ghost text-xs w-full"
                        onClick={() => act(() => api.post(`/florist/orders/${detail.id}/push-to-network`))}>
                  Un problème ? Confier au réseau Fleurinelle
                </button>
                <p className="text-[11px] text-muted">La plateforme conserve alors 100 % des frais de livraison.</p>
              </div>
            ) : detail.delivery_channel === "network" ? (
              <p className="text-xs text-muted" data-testid="network-note">Livraison assurée par le réseau Fleurinelle{detail.pushed_to_network ? " (confiée par vous)" : ""}.</p>
            ) : null}
            <details className="card p-3">
              <summary className="text-sm flex items-center gap-2 cursor-pointer" data-testid="issue-toggle"><AlertTriangle size={14} className="text-warning" />{t.problem}</summary>
              <div className="grid gap-2 mt-3">
                {IssueTypes.map(([k, label]) => (
                  <button key={k} data-testid={`issue-${k}`} className="btn-ghost text-[11px]" onClick={() => act(() => api.post("/florist/issues", { order_id: detail.id, type: k, description: label }))}>
                    {label}
                  </button>
                ))}
              </div>
            </details>
          </div>
        </div>
      ) : null}
    </div>
  );
}
